'use client'

import { type Post, FeaturedPostsBlock } from '@/payload-types'
import React, { useState } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/Callout'
import { EyebrowType } from '@/fields/eyebrow'
import { PostCard } from '@/components/PostCard'
import { Section } from '@/components/Section'

export function FeaturedPostsClient({
  posts,
  description,
  eyebrow,
  link,
}: {
  posts: Post[]
  description: FeaturedPostsBlock['description']
  eyebrow: EyebrowType
  link: FeaturedPostsBlock['link']
}) {
  const [enableNext, setEnableNext] = useState(true)
  const [enablePrev, setEnablePrev] = useState(false)

  const handlePrevClick = () => {
    const scrollElement = document.getElementById('posts-scroll')
    if (scrollElement) {
      scrollElement.scrollBy({ left: -300, behavior: 'smooth' })
      setTimeout(() => {
        checkPosition({ scrollElement, setEnablePrev, setEnableNext })
      }, 500)
    }
  }

  const handleNextClick = () => {
    const scrollElement = document.getElementById('posts-scroll')
    if (scrollElement) {
      scrollElement.scrollBy({ left: 300, behavior: 'smooth' })
      setTimeout(() => {
        checkPosition({ scrollElement, setEnablePrev, setEnableNext })
      }, 500)
    }
  }

  return (
    <Section bgClassName="bg-rock-1">
      <div className="flex flex-col md:flex-row w-full">
        <div className="flex-1">
          <Callout eyebrow={eyebrow} link={link} variant="dark" content={description} />
        </div>
        <div className="overflow-x-hidden pt-[40px] md:pb-[40px] md:py-0 md:flex-2 -mx-[calc(50vw-50%)] md:ml-0 md:-mr-[calc(calc(50vw-50%)-10px)]">
          <div className="flex gap-[30px] py-[2px] pl-[2px]">
            <div className="hidden md:flex flex-col gap-px z-10">
              <Button variant="olive" onClick={handlePrevClick} disabled={!enablePrev}>
                <ArrowLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </Button>
              <Button variant="olive" onClick={handleNextClick} disabled={!enableNext}>
                <ArrowRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </Button>
            </div>
            <div className="flex-1 overflow-x-hidden">
              <ScrollArea id="posts-scroll" className="w-full">
                <div className="flex space-x-6 pb-0 md:pb-[24px] pl-[10px] md:pl-0 pr-[10px]">
                  {posts.map((post) => {
                    return <PostCard post={post} key={post.id} />
                  })}
                </div>
              </ScrollArea>
            </div>
          </div>
        </div>
      </div>
    </Section>
  )
}

function checkPosition({
  scrollElement,
  setEnablePrev,
  setEnableNext,
}: {
  scrollElement: HTMLElement
  setEnablePrev: (value: boolean) => void
  setEnableNext: (value: boolean) => void
}) {
  if (scrollElement.scrollLeft === 0) {
    setEnablePrev(false)
  } else {
    setEnablePrev(true)
  }
  if (scrollElement.scrollLeft < scrollElement.scrollWidth - scrollElement.clientWidth) {
    setEnableNext(true)
  } else {
    setEnableNext(false)
  }
}
